import type { CoordinateSet } from "@/map/types/map";
import type { TLocation } from "@/locations/types/location";
import type { Machine } from "@/machines/types/machine";
import { AlertStatus, type Alert } from "@/alerts/types/machine-alert";
import { getLocationCentroid } from "./location-centroid";

const LOCATION_PIN_COLOR = "#2563eb"; // blue-600
const MACHINE_PIN_FALLBACK_COLOR = "#EF4444"; // red-500

/** Alert marker fill by status (mirrors the machine-detail map overlay palette). */
export const ALERT_STATUS_COLOR: Record<AlertStatus, string> = {
  [AlertStatus.Pending]: "#f59e0b", // amber
  [AlertStatus.Resolved]: "#22c55e", // green
  [AlertStatus.Ignored]: "#9ca3af", // gray
};

/** Freshly-arrived (unseen) alerts stand out in this color + a pulsing ring. */
export const NEW_ALERT_COLOR = "#ef4444"; // red-500

/** Selection id helpers — encoded into a marker's `selectionId`. */
export const locationSelectionId = (id: number) => `loc:${id}`;
export const machineSelectionId = (id: number) => `machine:${id}`;
export const alertSelectionId = (id: number | string) => `alert:${id}`;

export type MapSelection =
  | { type: "location"; id: number }
  | { type: "machine"; id: number }
  | { type: "alert"; id: string }
  | null;

/** Parse a marker id produced by the builders back into a typed selection. */
export const parseSelectionId = (markerId: string): MapSelection => {
  const [prefix, ...rest] = markerId.split(":");
  const raw = rest.join(":");
  if (!raw) return null;
  if (prefix === "loc") return { type: "location", id: Number(raw) };
  if (prefix === "machine") return { type: "machine", id: Number(raw) };
  if (prefix === "alert") return { type: "alert", id: raw };
  return null;
};

/** One single-point marker set per location, positioned at its machine centroid. */
export const buildLocationMarkers = (locations: TLocation[]): CoordinateSet[] =>
  locations
    .map((loc): CoordinateSet | null => {
      const centroid = getLocationCentroid(loc);
      if (!centroid) return null;
      return {
        name: loc.name,
        color: LOCATION_PIN_COLOR,
        path: [{ lat: centroid.lat, lng: centroid.lng }],
        selectionId: locationSelectionId(loc.id),
      };
    })
    .filter((s): s is CoordinateSet => s !== null);

/** One single-point marker set per machine at its last known GPS. */
export const buildMachineMarkers = (machines: Machine[]): CoordinateSet[] =>
  machines
    .filter((m) => m.last_gps?.latitude && m.last_gps?.longitude)
    .map(
      (m): CoordinateSet => ({
        name: m.name,
        color: m.color || MACHINE_PIN_FALLBACK_COLOR,
        path: [{ lat: m.last_gps.latitude, lng: m.last_gps.longitude }],
        isActive: m.status === "online",
        disableShadow: true,
        selectionId: machineSelectionId(m.id),
      }),
    );

/** Selected/new alert markers stack above every timestamp-ranked one. */
const ALERT_Z_HIGHLIGHT_BOOST = 10000;

/**
 * One selectable `alert`-type marker per geo-located alert, colored by status.
 * The marker matching `selectedAlertId` is flagged `isReviewed` so MapMarker
 * highlights it (ring + pulse). Markers are emitted oldest→newest with a
 * matching `zIndex`, so the newest alert always stacks on top of overlapping
 * pins (DOM order backs the z-index up where stacking contexts isolate it).
 */
export const buildAlertMarkers = (
  alerts: Alert[],
  selectedAlertId?: string | null,
  /** Ids of freshly-arrived alerts — drawn in {@link NEW_ALERT_COLOR} + highlighted. */
  newIds?: Set<number>,
): CoordinateSet[] =>
  alerts
    .filter((a) => a.gps?.latitude && a.gps?.longitude)
    .sort((a, b) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime())
    .map((a, rank): CoordinateSet => {
      const isNew = newIds?.has(a.id) ?? false;
      const isSelected = selectedAlertId != null && String(a.id) === selectedAlertId;
      return {
        name: isNew ? `${a.type} (new)` : a.type,
        color: isNew
          ? NEW_ALERT_COLOR
          : ALERT_STATUS_COLOR[a.status] ?? ALERT_STATUS_COLOR[AlertStatus.Pending],
        path: [{ lat: a.gps!.latitude, lng: a.gps!.longitude }],
        markerType: "alert",
        disablePolyline: true,
        disableShadow: true,
        // The pulsing-ring highlight marks both the selected alert and new arrivals.
        isReviewed: isNew || isSelected,
        // The selected alert also gets a distinct selected-color ring (see MapMarker).
        isSelected,
        zIndex: 1 + rank + (isNew || isSelected ? ALERT_Z_HIGHLIGHT_BOOST : 0),
        selectionId: alertSelectionId(a.id),
      };
    });
